import React, { useEffect, useState } from "react";
import {
  Container,
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  IconButton,
  InputAdornment,
  useTheme,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  Lock,
  Email as EmailIcon,
} from "@mui/icons-material";
import bgImage from "assets/bg.jpeg";
import logoImage from "assets/logo-prima.png";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchLogin } from "state/userActions";
import FloatingAlert from "components/FloatingAlert";
import { setMessage, setStatus } from "state";

const Login = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { accessToken: access_token } = useSelector((state) => state.user);
  const { status, message } = useSelector((state) => state.global);

  const [showPassword, setShowPassword] = useState(false);
  const [isFocused, setIsFocused] = useState({ email: false, password: false });
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  useEffect(() => {
    if (status === "success" && access_token) {
      navigate("/dashboard");
    }
  }, [status, access_token]);

  const handleCloseSnackbar = () => {
    dispatch(setStatus("default"));
    dispatch(setMessage(""));
  };
  return (
    <Container
      maxWidth="full"
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        padding: 2,
        backgroundImage: `url(${bgImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        position: "relative",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background:
            "linear-gradient(135deg, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0.5) 100%)",
          zIndex: 0,
        },
      }}
    >
      <Paper
        sx={{
          position: "relative",
          zIndex: 1,
          width: "100%",
          maxWidth: "420px",
          background: "rgba(255, 255, 255, 0.1)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          borderRadius: "24px",
          padding: { xs: "32px", sm: "40px" },
          border: "1px solid rgba(255, 255, 255, 0.2)",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
          animation: "fadeIn 0.5s ease-out",
          "@keyframes fadeIn": {
            from: { opacity: 0, transform: "translateY(20px)" },
            to: { opacity: 1, transform: "translateY(0)" },
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            marginBottom: 3,
          }}
        >
          <Box
            sx={{
              width: "200px",
              height: "130px",
              borderRadius: "16px",
              background: "rgba(255, 255, 255, 0.15)",
              backdropFilter: "blur(10px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "1px solid rgba(255, 255, 255, 0.2)",
            }}
          >
            <img
              src={logoImage}
              alt="Logo"
              style={{ width: "150px", height: "125px", objectFit: "contain" }}
            />
          </Box>
        </Box>
        <Box sx={{ textAlign: "center", marginBottom: 4 }}>
          <Typography
            variant="h4"
            sx={{
              color: "#fff",
              fontWeight: 700,
              marginBottom: 1,
              fontSize: { xs: "1.75rem", sm: "2rem" },
            }}
          >
            PRIMA CENTRE
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "rgba(255, 255, 255, 0.7)",
              fontSize: "0.95rem",
            }}
          >
            BACK OFFICE
          </Typography>
        </Box>
        <Box
          component="form"
          sx={{ display: "flex", flexDirection: "column", gap: 2 }}
        >
          <TextField
            className="texfieldLogin"
            label="Email"
            variant="outlined"
            fullWidth
            required
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onFocus={() => setIsFocused((prev) => ({ ...prev, email: true }))}
            onBlur={() => setIsFocused((prev) => ({ ...prev, email: false }))}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon
                    sx={{
                      color: isFocused.email
                        ? "#fff"
                        : "rgba(255, 255, 255, 0.5)",
                      transition: "color 0.2s ease",
                    }}
                  />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiInputLabel-root": {
                color: "rgba(255, 255, 255, 0.7)",
                "&.Mui-focused": { color: "#fff" },
              },
              "& .MuiOutlinedInput-root": {
                color: "#fff",
                "& fieldset": {
                  borderColor: "rgba(255, 255, 255, 0.3)",
                  borderWidth: "1.5px",
                },
                "&:hover fieldset": {
                  borderColor: "rgba(255, 255, 255, 0.5)",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#fff",
                  borderWidth: "2px",
                },
              },
              "& .MuiInputBase-input": {
                color: "#fff",
                "&::placeholder": {
                  color: "rgba(255, 255, 255, 0.5)",
                },
              },
              "& .MuiInputAdornment-root .MuiIconButton-root": {
                color: "rgba(255, 255, 255, 0.7)",
                "&:hover": { color: "#fff" },
              },
            }}
          />
          <TextField
            label="Password"
            type={showPassword ? "text" : "password"}
            variant="outlined"
            fullWidth
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onFocus={() =>
              setIsFocused((prev) => ({ ...prev, password: true }))
            }
            onBlur={() =>
              setIsFocused((prev) => ({ ...prev, password: false }))
            }
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                dispatch(fetchLogin(email, password));
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock
                    sx={{
                      color: isFocused.password
                        ? "#fff"
                        : "rgba(255, 255, 255, 0.5)",
                      transition: "color 0.2s ease",
                    }}
                  />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword((prev) => !prev)}
                    edge="end"
                    sx={{
                      color: "rgba(255, 255, 255, 0.7)",
                      "&:hover": { color: "#fff" },
                    }}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiInputLabel-root": {
                color: "rgba(255, 255, 255, 0.7)",
                "&.Mui-focused": { color: "#fff" },
              },
              "& .MuiOutlinedInput-root": {
                color: "#fff",
                "& fieldset": {
                  borderColor: "rgba(255, 255, 255, 0.3)",
                  borderWidth: "1.5px",
                },
                "&:hover fieldset": {
                  borderColor: "rgba(255, 255, 255, 0.5)",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#fff",
                  borderWidth: "2px",
                },
              },
              "& .MuiInputBase-input": {
                color: "#fff",
                "&::placeholder": {
                  color: "rgba(255, 255, 255, 0.5)",
                },
              },
              "& .MuiInputAdornment-root .MuiIconButton-root": {
                color: "rgba(255, 255, 255, 0.7)",
                "&:hover": { color: "#fff" },
              },
            }}
          />
          <Button
            variant="contained"
            fullWidth
            sx={{
              marginTop: 2,
              padding: "14px",
              fontSize: "1rem",
              fontWeight: 600,
              borderRadius: "12px",
              textTransform: "none",
              background: "linear-gradient(135deg, #932020 0%, #f1eef5 100%)",
              color: "#fff",
              boxShadow: "0 4px 15px rgba(225, 16, 16, 0.4)",
              transition: "all 0.3s ease",
              "&:hover": {
                background: "linear-gradient(135deg, #932020 0%, #f1eef5 100%)",
                boxShadow: "0 6px 20px rgba(179, 23, 23, 0.6)",
                transform: "translateY(-2px)",
              },
              "&:active": {
                transform: "translateY(0)",
              },
            }}
            onClick={() => {
              dispatch(fetchLogin(email, password));
            }}
          >
            Masuk
          </Button>
        </Box>
        <Box
          sx={{
            marginTop: 3,
            textAlign: "center",
            borderTop: "1px solid rgba(255, 255, 255, 0.1)",
            paddingTop: 2,
          }}
        >
          <Typography
            variant="caption"
            sx={{ color: "rgba(255, 255, 255, 0.5)", fontSize: "0.8rem" }}
          >
            &copy; {new Date().getFullYear()} Prima Centre. All rights reserved.
          </Typography>
        </Box>
      </Paper>
      <FloatingAlert
        open={status === "failed" && message.length > 0}
        onClose={handleCloseSnackbar}
        message={message}
        severity={"error"}
      />
    </Container>
  );
};

export default Login;
